/**
 * Header.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 5th Nov 2025
 * Updated: Version 2.32.1,
 */

import React, { useState, useEffect, useRef } from 'react';
import { useCart } from '../hooks/useCart';
import { useAuth } from '../context/AuthContext';
import { useVisibility } from '../context/VisibilityContext';
import { Link } from 'react-router-dom';
import { getIcon } from '../utils/icons';
import { useLanguage } from '../context/LanguageContext';
import LanguageSelector from './LanguageSelector';
import './Header.css';

const Header = ({ onCartClick }) => {
  const { items } = useCart();
  const { currentUser, logout } = useAuth();
  const { isPageVisible, isFeatureEnabled } = useVisibility();
  const { t, language } = useLanguage();
  // French/German nav labels are noticeably longer than English, so the masthead
  // needs tighter spacing and earlier collapse to avoid text colliding or clipping.
  const isLongLabelLanguage = !language.startsWith('en');
  // Scottish Gaelic runs longer still ("Mu dheidhinn", "Ainm-cleachdaiche"), so it
  // gets one extra tier of tightening on top of the shared long-label rules.
  const isExtraLongLabelLanguage = language === 'gd';
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const profileRef = useRef(null);

  // PWA Install Prompt Listener
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    closeMobileMenu();
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setIsInstallable(false);
      setDeferredPrompt(null);
    }
  };

  // Close profile dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [profileRef]);

  // Close mobile menu when window is resized wide enough that all nav items are visible
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1100) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    closeMobileMenu();
  };

  const toggleProfileDropdown = () => {
    setIsProfileOpen(!isProfileOpen);
  };

  return (
    <header
      className={`header${isLongLabelLanguage ? ' header--long-labels' : ''}${
        isExtraLongLabelLanguage ? ' header--xlong-labels' : ''
      }`}
      data-lang={language}
    >
      <div className="header-container">
        <div className="header-left">
          <Link to="/" className="header-title-link" onClick={closeMobileMenu}>
            <div className="logo-container">
              <div className="header-logo-stack">
                <img src="/images/android-chrome-192x192.png" alt="Cranford Publications Logo" className="fiddle-logo" />
                <span className="header-version-badge">v2.32.1</span>
              </div>
              <div className="header-title-container">
                <div className="header-title-line">Cranford</div>
                <div className="header-title-line">Publications</div>
              </div>
            </div>
          </Link>
        </div>

        <nav className="header-nav desktop-nav">
          <Link to="/" className="nav-link nav-home">{getIcon('home')} {t.nav.home}</Link>
          {isPageVisible('shop') && <Link to="/shop" className="nav-link nav-shop">{getIcon('shop')} {t.nav.shop}</Link>}
          <Link to="/articles" className="nav-link nav-articles">{getIcon('article')} {t.nav.articles}</Link>
          <Link to="/tunes" className="nav-link nav-tunes">🎵 {t.nav.tunes}</Link>
          <Link to="/page/abc-introduction" className="nav-link nav-abc">🎼 {t.nav.abc}</Link>
          {isPageVisible('about') && <Link to="/about" className="nav-link nav-about">{getIcon('about')} {t.nav.about}</Link>}
          {isPageVisible('contact') && <Link to="/contact" className="nav-link nav-contact">{getIcon('contact')} {t.nav.contact}</Link>}
        </nav>

        <div className="header-right">
          <div className="header-trailing-tools">
            <Link to="/search" className="header-tool-btn" aria-label={t.nav.search} title={t.nav.search}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <line x1="16.5" y1="16.5" x2="22" y2="22" />
              </svg>
              <span className="header-search-label">{t.nav.search}</span>
            </Link>
            <LanguageSelector />
            {isFeatureEnabled('shopping_enabled') && (
              <div className="cart-icon-wrapper" onClick={onCartClick} aria-label={t.shop.cart.openAria}>
                <span className="cart-icon">🛒</span>
                {totalItems > 0 && (
                  <span className="cart-item-count">{totalItems}</span>
                )}
              </div>
            )}
          </div>

          <div className="user-auth-section" ref={profileRef}>
            {currentUser ? (
              <div className="profile-container">
                <button
                  onClick={toggleProfileDropdown}
                  className="profile-button"
                  aria-label={t.nav.account}
                  title={`${t.nav.signedInAs} ${currentUser.username || 'User'}`}
                >
                  <span className="profile-icon" aria-hidden="true">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="2" fill="none" />
                      <path d="M4 20c0-3.3137 3.134-6 8-6s8 2.6863 8 6" stroke="currentColor" strokeWidth="2" fill="none" />
                    </svg>
                  </span>
                </button>
                {isProfileOpen && (
                  <div className="profile-dropdown">
                    <div className="profile-dropdown-header">
                      {t.nav.signedInAs} <br /><strong>{currentUser.username || 'User'}</strong>
                    </div>
                    <Link to="/profile" className="profile-dropdown-link" onClick={() => setIsProfileOpen(false)}>{t.nav.profile}</Link>
                    {currentUser && currentUser.roles && (currentUser.roles.includes('admin') || currentUser.roles.includes('super_admin')) && (
                      <Link to="/admin" className="profile-dropdown-link" onClick={() => setIsProfileOpen(false)}>{t.nav.admin}</Link>
                    )}
                    <button onClick={handleLogout} className="profile-dropdown-logout">{t.nav.logout}</button>
                  </div>
                )}
              </div>
            ) : (
              <div className="auth-links">
                <Link to="/register" className="nav-link auth-link">📝 {t.nav.signUp}</Link>
                <Link to="/login" className="nav-link auth-link">🔐 {t.nav.login}</Link>
              </div>
            )}
          </div>

          <button
            className="mobile-menu-button"
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
          >
            <span className={`hamburger ${isMobileMenuOpen ? 'open' : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
        </div>
      </div>

      <div className={`mobile-menu-overlay ${isMobileMenuOpen ? 'open' : ''}`} onClick={closeMobileMenu}></div>

      <div className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
        <div className="mobile-menu-content mobile-menu-content-nav-first">
          <nav className="mobile-nav">
            <Link to="/" className="mobile-nav-link" onClick={closeMobileMenu}>
              <span className="mobile-nav-icon">{getIcon('home')}</span>
              {t.nav.home}
            </Link>
            {isPageVisible('shop') && (
              <Link to="/shop" className="mobile-nav-link" onClick={closeMobileMenu}>
                <span className="mobile-nav-icon">{getIcon('shop')}</span>
                {t.nav.shop}
              </Link>
            )}
            <Link to="/articles" className="mobile-nav-link" onClick={closeMobileMenu}>
              <span className="mobile-nav-icon">{getIcon('article')}</span>
              {t.nav.articles}
            </Link>
            <Link to="/tunes" className="mobile-nav-link" onClick={closeMobileMenu}>
              <span className="mobile-nav-icon">🎵</span>
              {t.nav.tunes}
            </Link>
            <Link to="/page/abc-introduction" className="mobile-nav-link" onClick={closeMobileMenu}>
              <span className="mobile-nav-icon">🎼</span>
              {t.nav.abc}
            </Link>
            {isPageVisible('about') && (
              <Link to="/about" className="mobile-nav-link" onClick={closeMobileMenu}>
                <span className="mobile-nav-icon">{getIcon('about')}</span>
                {t.nav.about}
              </Link>
            )}
            {isPageVisible('contact') && (
              <Link to="/contact" className="mobile-nav-link" onClick={closeMobileMenu}>
                <span className="mobile-nav-icon">{getIcon('contact')}</span>
                {t.nav.contact}
              </Link>
            )}
            <Link to="/faq" className="mobile-nav-link" onClick={closeMobileMenu}>
              <span className="mobile-nav-icon">❓</span>
              {t.nav.faq}
            </Link>
            {isInstallable && isMobileDevice && (
              <button
                className="mobile-nav-link"
                onClick={handleInstallClick}
                style={{ background: 'none', border: 'none', textAlign: 'left', width: '100%', cursor: 'pointer', color: 'var(--primary-color)', fontWeight: 'bold' }}
              >
                <span className="mobile-nav-icon">📱</span>
                {t.nav.installApp}
              </button>
            )}
          </nav>

          <div className="mobile-auth-section">
            {currentUser ? (
              <>
                <div className="mobile-user-info">
                  <span className="mobile-welcome-user">👤 {t.nav.welcome}, {currentUser.username}</span>
                  {currentUser && currentUser.roles && currentUser.roles.length > 0 && (
                    <span className="mobile-user-role">
                      ({currentUser.roles[0].replace('_', ' ')})
                    </span>
                  )}
                </div>
                <Link to="/profile" className="mobile-nav-link" onClick={closeMobileMenu}>
                  <span className="mobile-nav-icon">{getIcon('login')}</span>
                  {t.nav.profile}
                </Link>
                {currentUser && currentUser.roles && (currentUser.roles.includes('admin') || currentUser.roles.includes('super_admin')) && (
                  <Link to="/admin" className="mobile-nav-link admin-link" onClick={closeMobileMenu}>
                    <span className="mobile-nav-icon">{getIcon('admin')}</span>
                    {t.nav.admin}
                  </Link>
                )}
                <button onClick={handleLogout} className="mobile-logout-button">
                  <span className="mobile-nav-icon">{getIcon('logout')}</span>
                  {t.nav.logout}
                </button>
              </>
            ) : (
              <>
                <Link to="/register" className="mobile-nav-link" onClick={closeMobileMenu}>
                  <span className="mobile-nav-icon">📝</span>
                  {t.nav.signUp}
                </Link>
                <Link to="/login" className="mobile-nav-link" onClick={closeMobileMenu}>
                  <span className="mobile-nav-icon">🔐</span>
                  {t.nav.login}
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
